import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription, catchError, forkJoin, map, of } from 'rxjs';
import { ContactsService } from 'src/app/private/services/contacts.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import {
  LEAD_SOURCE_OPTIONS,
  isSelectLeadField,
  normalizeFieldOptions,
} from 'src/app/utils/lead-field-options.util';
import { capitalizeName } from 'src/app/utils/name-format.util';

interface ProfileField {
  label: string;
  key: string;
  group: 'root' | 'address' | 'custom';
  wide?: boolean;
  inputType?: string;
  readOnly?: boolean;
  options?: string[];
}

interface ProfileSection {
  id: string;
  title: string;
  icon: string;
  fields: ProfileField[];
}

@Component({
  selector: 'app-contact-profile-page',
  templateUrl: './contact-profile-page.component.html',
  styleUrls: ['./contact-profile-page.component.scss'],
})
export class ContactProfilePageComponent implements OnInit, OnDestroy {
  loading = true;
  notFound = false;
  contact: any = null;
  sections: ProfileSection[] = [];
  expanded = new Set<string>(['details', 'address', 'custom']);
  saving = false;
  sourceOptions: string[] = [...LEAD_SOURCE_OPTIONS];
  private routeSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contactsApi: ContactsService,
    private localStorage: LocalStorageService,
    private toast: ToastrService
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.paramMap.subscribe((params) => {
      const contactId = params.get('contactId');
      if (!contactId) {
        this.router.navigate(['/main/contacts']);
        return;
      }
      this.loadContact(contactId);
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  goBack(): void {
    this.router.navigate(['/main/contacts']);
  }

  toggleSection(id: string): void {
    if (this.expanded.has(id)) {
      this.expanded.delete(id);
      return;
    }
    this.expanded.add(id);
  }

  isExpanded(id: string): boolean {
    return this.expanded.has(id);
  }

  fieldValue(field: ProfileField): string {
    if (field.group === 'address') {
      return this.contact?.address?.[field.key] ?? '';
    }
    if (field.group === 'custom') {
      const value = this.contact?.fields?.[field.key];
      if (value == null) {
        return '';
      }
      return typeof value === 'object' ? JSON.stringify(value) : String(value);
    }
    return this.contact?.[field.key] ?? '';
  }

  setFieldValue(field: ProfileField, value: string): void {
    if (field.readOnly || !this.contact) {
      return;
    }
    if (field.group === 'address') {
      if (!this.contact.address || typeof this.contact.address !== 'object') {
        this.contact.address = {};
      }
      this.contact.address[field.key] = value;
      return;
    }
    if (field.group === 'custom') {
      if (!this.contact.fields || typeof this.contact.fields !== 'object' || Array.isArray(this.contact.fields)) {
        this.contact.fields = {};
      }
      this.contact.fields[field.key] = value;
      return;
    }
    this.contact[field.key] = value;
  }

  updateDetails(): void {
    if (!this.contact?.contact_id || this.saving) {
      return;
    }
    const firstName = capitalizeName(String(this.contact.first_name || '').trim());
    const lastName = capitalizeName(String(this.contact.last_name || '').trim());
    if (!firstName) {
      this.toast.error('First name is required');
      return;
    }

    this.contact.first_name = firstName;
    this.contact.last_name = lastName;
    this.saving = true;

    const payload = {
      contact_id: this.contact.contact_id,
      client_id: this.localStorage.getClientId(),
      center_id: this.contact.center_id || this.localStorage.getCenterId(),
      contact_owner: this.contact.contact_owner,
      user_id: this.contact.user_id,
      account_id: this.contact.account_id,
      first_name: firstName,
      last_name: lastName,
      fax: this.contact.fax || '',
      title: this.contact.title || '',
      department: this.contact.department || '',
      email: this.contact.email || '',
      secondary_email: this.contact.secondary_email || '',
      skype_id: this.contact.skype_id || '',
      description: this.contact.description || '',
      phone: this.contact.phone || '',
      other_phone: this.contact.other_phone || '',
      mobile: this.contact.mobile || '',
      country_code: this.contact.country_code || '',
      address: this.contact.address || {},
      lead_source: this.contact.lead_source || '',
      dob: this.contact.dob || '',
      fields: this.contact.fields || {},
    };

    this.contactsApi.updateContact(payload).subscribe({
      next: () => {
        this.saving = false;
        this.toast.success('Contact updated successfully');
      },
      error: (error) => {
        this.saving = false;
        const message = error?.error?.message || error?.error?.errors?.[0]?.message;
        this.toast.error(message || 'Error while updating contact');
      },
    });
  }

  get displayName(): string {
    const name = [this.contact?.first_name, this.contact?.last_name]
      .filter(Boolean)
      .join(' ')
      .trim();
    return name || 'Contact';
  }

  get initials(): string {
    const parts = this.displayName.split(/\s+/).filter(Boolean);
    const letters = parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase());
    return letters.join('') || 'C';
  }

  get displayEmail(): string {
    return this.contact?.email || this.contact?.secondary_email || '—';
  }

  get displayPhone(): string {
    const number = this.contact?.mobile || this.contact?.phone || this.contact?.other_phone;
    if (!number) return '—';
    const countryCode = String(this.contact?.country_code || '').replace(/\D/g, '');
    const digits = String(number).replace(/\D/g, '');
    if (countryCode && digits && !digits.startsWith(countryCode)) {
      return `+${countryCode} ${digits}`;
    }
    return String(number);
  }

  get displaySource(): string {
    return this.contact?.lead_source || this.contact?.source || '—';
  }

  get ownerName(): string {
    const owner = [this.contact?.user?.prefix, this.contact?.user?.fname, this.contact?.user?.lname]
      .filter(Boolean)
      .join(' ')
      .trim();
    return owner || this.contact?.user?.email || '—';
  }

  private loadContact(contactId: string): void {
    this.loading = true;
    this.notFound = false;
    this.contact = null;
    this.sections = [];

    const clientId = this.localStorage.getClientId();
    const contact$ = this.contactsApi
      .getAllContacts({
        page: 0,
        limit: 1,
        where: {
          client_id: clientId,
          contact_id: contactId,
        },
      })
      .pipe(
        map((response: any) => response?.rows?.[0] ?? null),
        catchError(() => of(null))
      );
    const fields$ = this.contactsApi.getContactFields({ client_id: clientId }).pipe(
      map((response: any) => (Array.isArray(response?.fields) ? response.fields : [])),
      catchError(() => of([]))
    );

    forkJoin({ contact: contact$, fields: fields$ }).subscribe({
      next: ({ contact, fields }) => {
        this.loading = false;
        if (!contact) {
          this.notFound = true;
          return;
        }
        this.contact = contact;
        this.contact.lead_source = this.contact.lead_source || '';
        this.sourceOptions = this.withCurrentOption(LEAD_SOURCE_OPTIONS, this.contact.lead_source);
        this.sections = this.buildSections(contact, fields);
      },
      error: () => {
        this.loading = false;
        this.notFound = true;
      },
    });
  }

  private buildSections(contact: any, fieldDefs: any[]): ProfileSection[] {
    const labels = new Map<string, string>();
    fieldDefs.forEach((field) => {
      if (field?.field_name) {
        labels.set(String(field.field_name), field.label || this.formatLabel(field.field_name));
      }
    });

    if (!contact.address || typeof contact.address !== 'object' || Array.isArray(contact.address)) {
      contact.address = {};
    }
    if (!contact.fields || typeof contact.fields !== 'object' || Array.isArray(contact.fields)) {
      contact.fields = {};
    }

    const root = (label: string, key: string, extra: Partial<ProfileField> = {}): ProfileField => ({
      label,
      key,
      group: 'root',
      ...extra,
    });
    const details: ProfileField[] = [
      root('First Name', 'first_name'),
      root('Last Name', 'last_name'),
      root('Title', 'title'),
      root('Department', 'department'),
      root('Email', 'email', { inputType: 'email' }),
      root('Secondary Email', 'secondary_email', { inputType: 'email' }),
      root('Phone', 'phone'),
      root('Country Code', 'country_code'),
      root('Mobile', 'mobile'),
      root('Other Phone', 'other_phone'),
      root('Fax', 'fax'),
      root('Skype', 'skype_id'),
      root('Date of Birth', 'dob', { inputType: 'date' }),
      root('Source', 'lead_source', {
        options: this.withCurrentOption(LEAD_SOURCE_OPTIONS, contact.lead_source),
      }),
      root('Description', 'description', { wide: true }),
    ];

    const addressFields: ProfileField[] = [
      { label: 'Street', key: 'street', group: 'address' },
      { label: 'City', key: 'city', group: 'address' },
      { label: 'State', key: 'state', group: 'address' },
      { label: 'Postal Code', key: 'postal_code', group: 'address' },
      { label: 'Country', key: 'country', group: 'address' },
    ];

    const customFields: ProfileField[] = [];
    const custom = contact.fields;
    const orderedKeys = [
      ...fieldDefs.map((field) => field?.field_name).filter(Boolean),
      ...Object.keys(custom),
    ];
    const seen = new Set<string>();
    orderedKeys.forEach((key) => {
      const name = String(key);
      if (seen.has(name) || name === 'address') {
        return;
      }
      seen.add(name);
      const value = custom[name];
      const isObject = value != null && typeof value === 'object';
      if (isObject && this.isAddressObject(value)) {
        return;
      }
      const def = fieldDefs.find((field) => field?.field_name === name);
      const options = isSelectLeadField(def)
        ? this.withCurrentOption(normalizeFieldOptions(def?.data), value)
        : undefined;
      customFields.push({
        label: labels.get(name) || this.formatLabel(name),
        key: name,
        group: 'custom',
        wide: !options?.length && (isObject || def?.type === 'textarea'),
        inputType: options?.length
          ? 'select'
          : def?.type === 'date'
            ? 'date'
            : def?.type === 'email'
              ? 'email'
              : 'text',
        readOnly: isObject,
        options,
      });
    });

    const sections: ProfileSection[] = [
      { id: 'details', title: 'Contact Information', icon: 'fa-user', fields: details },
      { id: 'address', title: 'Address', icon: 'fa-map-marker', fields: addressFields },
    ];
    if (customFields.length) {
      sections.push({
        id: 'custom',
        title: 'Additional Fields',
        icon: 'fa-list-alt',
        fields: customFields,
      });
    }
    return sections;
  }

  private isAddressObject(value: Record<string, unknown>): boolean {
    return ['street', 'city', 'state', 'country', 'postal_code'].some((key) =>
      Object.prototype.hasOwnProperty.call(value, key)
    );
  }

  private withCurrentOption(options: string[], current: unknown): string[] {
    const list = [...options];
    const value = current == null || typeof current === 'object' ? '' : String(current);
    if (value && !list.includes(value)) {
      list.unshift(value);
    }
    return list;
  }

  private formatLabel(key: string): string {
    return String(key || '')
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
}
